import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Archive } from "lucide-react";
import { api, getUser } from "../lib/api";
import { Page, EmptyState, UrgencyChip, StatusChip, Spinner } from "../components/Brand";
import { formatDateFr, timeAgoFr } from "../lib/fr";
import { Toaster } from "sonner";

export default function IMDashboard() {
  const navigate = useNavigate();
  const user = getUser();
  const [showArchived, setShowArchived] = useState(false);

  const { data: refs, isLoading } = useQuery({
    queryKey: ["my-referrals", showArchived],
    queryFn: async () =>
      (await api.get(`/referrals/mine${showArchived ? "?include_archived=true" : ""}`)).data,
  });

  return (
    <Page>
      <Toaster position="top-center" richColors />
      <div className="flex items-end justify-between mb-6 md:mb-8 flex-wrap gap-3">
        <div>
          <div className="cs-label">Médecine Interne</div>
          <h1 className="font-semibold mt-1" style={{ fontSize: "32px", letterSpacing: "-0.6px" }}>
            Bonjour {user?.name?.split(" ")[1] || ""}
          </h1>
          <div className="text-[14px] mt-1" style={{ color: "var(--cs-text-muted)" }}>
            Vos références transmises à l'OB-GYN
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowArchived((v) => !v)}
            className={showArchived ? "cs-btn-primary text-[13px]" : "cs-btn-ghost text-[13px]"}
            data-testid="toggle-archived"
          >
            <Archive size={15} /> {showArchived ? "Masquer archivées" : "Afficher archivées"}
          </button>
          <button
            onClick={() => navigate("/im/new")}
            className="cs-btn-primary"
            data-testid="new-referral-button"
          >
            <Plus size={18} /> Nouvelle référence
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="cs-card p-10 flex justify-center"><Spinner /></div>
      ) : !refs?.length ? (
        <EmptyState
          title="Aucune référence pour le moment"
          hint="Créez votre première référence vers l'OB-GYN."
          action={
            <button onClick={() => navigate("/im/new")} className="cs-btn-primary" data-testid="empty-new-referral">
              <Plus size={18} /> Créer une référence
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="im-dashboard">
          {refs.map((r) => (
            <button
              key={r.id}
              onClick={() => navigate(`/referral/${r.id}`)}
              className="cs-card p-5 text-left hover:-translate-y-0.5 transition-transform"
              style={{ transition: "transform .15s, box-shadow .15s", opacity: r.archived ? 0.6 : 1 }}
              data-testid={`referral-card-${r.referral_code}`}
            >
              <div className="flex items-start justify-between gap-3 pb-3 border-b" style={{ borderColor: "var(--cs-border)" }}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[17px] tracking-tight">{r.referral_code}</span>
                    {r.archived && (
                      <span className="cs-chip" style={{ background: "var(--cs-bg)", color: "var(--cs-text-muted)", borderColor: "var(--cs-border)" }}>Archivée</span>
                    )}
                  </div>
                  <div className="text-[12px] mt-0.5" style={{ color: "var(--cs-text-subtle)" }}>
                    Créée {timeAgoFr(r.created_at)}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <StatusChip value={r.status} />
                  <UrgencyChip value={r.urgency} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-y-2.5 gap-x-3 pt-3 text-[13px]">
                <Field label="Diagnostic" value={r.diagnosis === "cardiomyopathy" ? "Cardiomyopathie" : "CMP péripartum"} />
                <Field label="FEVG" value={r.ef_range + " %"} />
                <Field label="Âge" value={r.age_range + " ans"} />
                <Field label="Grossesse" value={pregLabel(r.pregnancy_status)} />
                {r.appointment_date && (
                  <div className="col-span-2 mt-2 p-3 rounded-[12px]" style={{ background: "rgba(124,179,66,0.08)", borderLeft: "3px solid var(--cs-primary)" }}>
                    <div className="text-[12px]" style={{ color: "var(--cs-primary-dark)", fontWeight: 600 }}>
                      {formatDateFr(r.appointment_date)} · {r.appointment_time}
                    </div>
                    <div className="text-[12px] mt-0.5" style={{ color: "var(--cs-text-muted)" }}>
                      {r.location} · {r.provider}
                    </div>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </Page>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="cs-label">{label}</div>
      <div className="font-medium mt-0.5" style={{ color: "var(--cs-text)" }}>{value}</div>
    </div>
  );
}

function pregLabel(v) {
  return ({ not_pregnant: "Non enceinte", pregnant_t1: "T1", pregnant_t2: "T2", pregnant_t3: "T3", postpartum: "Post-partum" })[v] || v;
}
